import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import * as fromRoot from "../../reducers";
import { DiagnosisDescriptionsService } from "../diagnosis/diagnosis-descriptions.service";
import { PlanDescriptionsService } from "../plan/plan-descriptions.service";
import { Observable } from "rxjs/Observable";
import {
  Diagnosis360,
  DiagnosisItem,
  FacialOptions,
  MalocclusionsOptions,
  TmjOptions,
} from "../diagnosis/diagnosis.model";
import { Discount, PlanItem, PlanStage } from "../plan/plan.model";
import { Patient } from "../../patient/patient.model";
import { Settings } from "../../settings/settings.model";
import { DiagnosisWidgetService } from "../diagnosis/diagnosis-widget.service";
import { PlanWidgetService } from "../plan/plan-widget.service";
import { PlanTreatmentsService } from "@app/core/store/plan-editor/plan/plan-treatments.service";
import "rxjs-spy/add/operator/tag";
import { ReplaySubject } from "rxjs/ReplaySubject";
import { tap } from "rxjs/operators";
import { PlanCalculations } from "@app/shared/utils/plan-calculations";

@Injectable()
export class ReportPreviewService {
  diagnosisItems$: Observable<DiagnosisItem[]>;
  planItems$: Observable<PlanItem[]>;
  planStages$: Observable<PlanStage[]>;

  customTotalText$: Observable<string>;
  discount$: Observable<Discount>;

  diagnosis360$: Observable<Diagnosis360>;
  orthoMalocclusions$: Observable<MalocclusionsOptions>;
  orthoFacial$: Observable<FacialOptions>;
  orthoTMJ$: Observable<TmjOptions>;

  reportPatient$: Observable<Patient>;
  reportName$: Observable<string>;

  userSettings$: Observable<Settings>;

  diagnosis$ = new ReplaySubject();
  plan$ = new ReplaySubject();
  settings$ = new ReplaySubject();

  constructor(
    private store: Store<fromRoot.AppState>,
    private diagnosisWidgetService: DiagnosisWidgetService,
    private planWidgetService: PlanWidgetService,
    private diagnosisDescriptionsService: DiagnosisDescriptionsService,
    private planDescriptionsService: PlanDescriptionsService,
    private treatmentsService: PlanTreatmentsService,
  ) {
    this.diagnosisItems$ = store.select(fromRoot.getDiagnosisItems);
    this.planItems$ = store.select(fromRoot.getPlanItems);
    this.planStages$ = store.select(fromRoot.getPlanStages);

    this.customTotalText$ = store.select(fromRoot.getPlanCustomTotalText);
    this.discount$ = store.select(fromRoot.getPlanDiscount);
    this.reportName$ = store.select(fromRoot.getPlanEditorCurrentPlanName);
    this.reportPatient$ = store.select(fromRoot.getPlanEditorCurrentPatient);

    this.diagnosis360$ = store.select(fromRoot.getDiagnosis360);
    this.orthoMalocclusions$ = store.select(
      fromRoot.getDiagnosisOrthoMalocclusions,
    );
    this.orthoFacial$ = store.select(fromRoot.getDiagnosisOrthoFacial);
    this.orthoTMJ$ = store.select(fromRoot.getDiagnosisOrthoTMJ);

    this.userSettings$ = store.select(fromRoot.getSettings);

    /* Diagnosis */
    Observable.combineLatest(
      // TODO: shoudln't the odontogram be fed with the diagnosisItems input? because in the report configurator user may delete items?
      this.diagnosis360$
        .filter((diagnosis) => diagnosis !== null)
        .tag("report/diagnosis/360"),
      this.diagnosisWidgetService
        .getOdontogram()
        .tag("report/diagnosis/odontogram"),
      this.diagnosisItems$.tag("report/diagnosis/items"),
      this.diagnosisItems$
        .flatMap((items) =>
          this.diagnosisDescriptionsService.getDescriptions(items),
        )
        .tag("report/diagnosis/itemDescriptions"),
      this.orthoMalocclusions$.tag("report/diagnosis/orthoMalocclusions"),
      this.orthoFacial$.tag("report/diagnosis/orthoFacial"),
      this.orthoTMJ$.tag("report/diagnosis/orthoTMJ"),
      this.diagnosisDescriptionsService.getDescriptionsObservable(),
      (
        diagram360,
        odontogram,
        items,
        descriptions,
        malocclusions,
        facial,
        tmj,
        allDescriptions,
      ) => {
        let orthoDescriptions = {
          ...this.diagnosisDescriptionsService.getOrthoDescriptions(
            "malocclusions",
            malocclusions,
            allDescriptions,
          ),
          ...this.diagnosisDescriptionsService.getOrthoDescriptions(
            "facial",
            facial,
            allDescriptions,
          ),
          ...this.diagnosisDescriptionsService.getOrthoDescriptions(
            "tmj",
            tmj,
            allDescriptions,
          ),
        };

        return {
          diagram360: diagram360,
          odontogram: odontogram,
          items: items,
          descriptions: descriptions,
          ortho: {
            malocclusions: malocclusions,
            facial: facial,
            tmj: tmj,
            descriptions: orthoDescriptions,
          },
        };
      },
    )
      .debounceTime(1000)
      .tag("report/diagnosis")
      .subscribe(this.diagnosis$);

    /* Plan */
    Observable.combineLatest(
      // TODO: shoudln't the odontogram be fed with the diagnosisItems input? because in the report configurator user may delete items?
      this.planWidgetService.getOdontogram().tag("report/plan/odontogram"),
      this.getPlanItems().tag("report/plan/items"),
      this.planItems$
        .flatMap((items) => this.planDescriptionsService.getDescriptions(items))
        .tag("report/plan/descriptions"),
      this.planStages$,
      this.customTotalText$,
      this.discount$,
      (odontogram, items, descriptions, stages, customTotalText, discount) => {
        return {
          odontogram,
          items,
          descriptions,
          stages: stages.map((stage) => {
            return Object.assign({}, stage, {
              items: stage.items.map((item) => {
                const conditionClass =
                  this.treatmentsService.getClassForTreatment(item.treatment);
                return Object.assign({}, item, {
                  treatment: conditionClass.getNameIdentifier(item),
                });
              }),
              ...PlanCalculations.calculateStageTotals(stage),
            });
          }),
          ...PlanCalculations.calculatePlanTotals(stages, discount),
          customTotalText,
          discount,
        };
      },
    )
      .debounceTime(1000)
      .tag("report/plan")
      .subscribe(this.plan$);

    /* Settings */
    Observable.combineLatest(
      this.reportName$,
      this.reportPatient$.filter((patient) => patient !== null),
      this.userSettings$.filter((settings) => settings !== null),
      (name, patient, userSettings) => {
        return {
          name: name,
          patient: patient.name,
          currency: userSettings.currency,
          numberingSystem: userSettings.numberingSystem,
          defaultColor: userSettings.defaultColor,
          doctor: {
            name: userSettings.name,
            logo: userSettings.logo,
            contact: userSettings.contactInfo,
          },
        };
      },
    )
      .tag("report/settings")
      .subscribe(this.settings$);
  }

  /*
   * Diagnosis
   */

  getDiagnosis() {
    return this.diagnosis$;
  }

  /*
   * Plan
   */

  getPlan() {
    return this.plan$;
  }

  /**
   * Transforms the treatment identifier to show in the final report because some treatments have different names depending on the options
   */
  private getPlanItems() {
    return this.planItems$.switchMap((items) => {
      return Observable.of(
        items.map((item) => {
          const conditionClass = this.treatmentsService.getClassForTreatment(
            item.treatment,
          );
          return Object.assign({}, item, {
            treatment: conditionClass.getNameIdentifier(item),
          });
        }),
      );
    });
  }

  /*
   * Other Report Settings
   */

  getSettings() {
    return this.settings$;
  }
}



// WEBPACK FOOTER //
// /app/src/app/core/store/plan-editor/report/report-preview.service.ts